// src/easymap/mapcontrols/SupportTool/SupportTool.tsx
import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBug, faLightbulb, faQuestion } from '@fortawesome/free-solid-svg-icons';
import { faGithub } from '@fortawesome/free-brands-svg-icons';
import './SupportTool.scss';
import ContextHelp from '../../ContextHelp/ContextHelp';
import { useFusionAuth } from '@fusionauth/react-sdk';
import { getUserById } from '../../../services/fusionAuthService';
import DOMPurify from 'dompurify';
import { getBackendUrl } from '../../../services/configService';
import { markdownToHtml } from './markdownToHtml';

/**
 * Render the issue body. Prefer GitHub's pre-rendered body_html (which
 * contains short-lived signed image URLs for any pasted screenshots);
 * fall back to the local markdown converter for older entries that
 * lack it.
 */
const renderIssueBody = (issue: { body?: string | null; bodyHtml?: string | null }): string => {
  if (issue.bodyHtml) {
    return DOMPurify.sanitize(issue.bodyHtml, {
      ADD_TAGS: ['img'],
      ADD_ATTR: ['src', 'alt', 'target', 'rel'],
    });
  }
  return markdownToHtml(issue.body || '');
};

interface SupportToolProps {
  map?: any;
  initialType?: 'default' | 'openIssues' | 'bug' | 'feature' | 'support';
  embedded?: boolean;
  onClose?: () => void;
}

// Backend accepts up to 50 MB per attached file (multer limit in githubRouter).
const MAX_FILE_BYTES = 50 * 1024 * 1024;

const SupportTool: React.FC<SupportToolProps> = ({
  map,
  initialType = 'support',
  embedded = false,
  onClose,
}) => {
  const { userInfo } = useFusionAuth();
  const [userDetails, setUserDetails] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [supportType, setSupportType] = useState(initialType);
  const [openIssues, setOpenIssues] = useState<any[]>([]);
  const [fetchingIssues, setFetchingIssues] = useState(false);
  const [response, setResponse] = useState<any>(null);
  const [submitting, setSubmitting] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    stepsToReproduce: '',
    expectedBehavior: '',
    actualBehavior: '',
    email: '',
    name: '',
  });

  // load user and prefill
  useEffect(() => {
    const fetchUserDetails = async (userId: string) => {
      if (!userId) return;
      setLoading(true);
      try {
        const user = await getUserById(userId);
        setUserDetails(user);
        setFormData((prev) => ({
          ...prev,
          email: user.email,
          name: `${user.firstName} ${user.lastName}`,
        }));
      } catch (error) {
        console.error('Error fetching user details:', error);
      } finally {
        setLoading(false);
      }
    };

    if (userInfo && userInfo.sub) {
      fetchUserDetails(userInfo.sub);
    }
  }, [userInfo]);

  // Track support tool view opens and load issues if needed
  useEffect(() => {
    setSupportType(initialType);
    if (initialType !== 'default') {
      const token = localStorage.getItem('access_token');
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      fetch(`${getBackendUrl()}/api/support/track`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ view: initialType }),
      }).catch(() => {}); // fire-and-forget
    }
    if (initialType === 'openIssues') {
      fetchOpenIssues();
    }
  }, [initialType]);

  // When the user switches to the bug or feature form, surface the existing
  // open issues beneath it so they can check for duplicates before filing.
  useEffect(() => {
    if (supportType === 'bug' || supportType === 'feature') {
      fetchOpenIssues();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supportType]);

  const handleCloseModal = () => {
    // this component is always rendered inside a modal now, so just tell parent
    if (onClose) {
      onClose();
    }
    // reset internal state for next time
    setSupportType(initialType);
    setResponse(null);
    setAttachments([]);
    setAttachmentError(null);
    setFormData({
      title: '',
      description: '',
      stepsToReproduce: '',
      expectedBehavior: '',
      actualBehavior: '',
      email: userDetails?.email || '',
      name: userDetails ? `${userDetails.firstName} ${userDetails.lastName}` : '',
    });
  };

  const fetchOpenIssues = async () => {
    setFetchingIssues(true);
    try {
      const url = `${getBackendUrl()}/api/support/issues`;
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      const token = localStorage.getItem('access_token');
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response = await fetch(url, {
        method: 'GET',
        headers,
        credentials: 'include',
      });
      if (response.ok) {
        const issues = await response.json();
        setOpenIssues(issues.data);
      } else {
        console.error('Error fetching open issues:', await response.text());
      }
    } catch (error) {
      console.error('Error fetching open issues:', error);
    } finally {
      setFetchingIssues(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const url = `${getBackendUrl()}/api/support/submit`;
      const payload = {
        type: supportType,
        ...formData,
      };
      const submitHeaders: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      const token = localStorage.getItem('access_token');
      if (token) {
        submitHeaders['Authorization'] = `Bearer ${token}`;
      }
      const res = await fetch(url, {
        method: 'POST',
        headers: submitHeaders,
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok) {
        setResponse(data);
      } else {
        console.error('Submission failed:', data);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files ? Array.from(e.target.files) : [];
    const oversized = selected.filter((file) => file.size > MAX_FILE_BYTES);
    if (oversized.length > 0) {
      setAttachmentError(
        `These files exceed the 50 MB limit and were not attached: ${oversized
          .map((file) => file.name)
          .join(', ')}`,
      );
    } else {
      setAttachmentError(null);
    }
    setAttachments(selected.filter((file) => file.size <= MAX_FILE_BYTES));
  };

  const handleSupportSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const url = `${getBackendUrl()}/api/support/submit`;
      // Multipart body so attachments ride along; do NOT set Content-Type —
      // the browser adds the multipart boundary itself.
      const body = new FormData();
      body.append('type', 'support');
      body.append('title', formData.title);
      body.append('name', formData.name);
      body.append('email', formData.email);
      body.append('description', formData.description);
      attachments.forEach((file) => body.append('attachments', file));

      const submitHeaders: Record<string, string> = {};
      const token = localStorage.getItem('access_token');
      if (token) {
        submitHeaders['Authorization'] = `Bearer ${token}`;
      }
      const res = await fetch(url, {
        method: 'POST',
        headers: submitHeaders,
        credentials: 'include',
        body,
      });
      const data = await res.json();
      if (res.ok) {
        setResponse(data);
      } else {
        console.error('Submission failed:', data);
      }
    } catch (error) {
      console.error('Error submitting support request:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const titleFor = (t: string) => {
    switch (t) {
      case 'openIssues':
        return 'Open Issues';
      case 'bug':
        return 'Report an Issue';
      case 'feature':
        return 'Request a Feature';
      case 'support':
        return 'Support Request';
      default:
        return 'IntelliFire Support';
    }
  };

  // Reusable open-issues list — shown standalone and beneath the bug/feature
  // forms so users can spot duplicates before filing.
  const openIssuesList = (
    <div className="open-issues">
      {fetchingIssues ? (
        <p>Loading issues...</p>
      ) : openIssues.length > 0 ? (
        <ul className="issues-list">
          {openIssues.map((issue, index) => (
            <li key={index} className="issue-item">
              <h4>
                <FontAwesomeIcon
                  icon={
                    issue.labels && issue.labels.length > 0
                      ? issue.labels[0].name.toLowerCase().includes('bug')
                        ? faBug
                        : issue.labels[0].name.toLowerCase().includes('feature') ||
                          issue.labels[0].name.toLowerCase().includes('enhancement')
                        ? faLightbulb
                        : faQuestion
                      : faQuestion
                  }
                  className="issue-icon"
                />
                {issue.title}
              </h4>
              <span className="issue-number"><a href={issue.link} target="_blank" rel="noopener noreferrer">#{issue.number}</a> :: </span>
              <span className="issue-type">
                {issue.labels && issue.labels.length > 0
                  ? issue.labels[0].name
                  : 'Unknown'}{' '}
                ::{' '}
              </span>
              <span className="issue-status">{issue.state}</span>

              <div className="issue-description">
                <details>
                  <summary>Show details</summary>
                  <div
                    className="issue-description-body"
                    dangerouslySetInnerHTML={{ __html: renderIssueBody(issue) }}
                    ></div>
                </details>
              </div>
              {issue.link && (
                <a href={issue.link} target="_blank" rel="noopener noreferrer">
                  <FontAwesomeIcon icon={faGithub} className="issue-link-icon" />
                  Issue Link
                </a>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p>No open issues found.</p>
      )}
    </div>
  );

  const content = (
    <div
      className={`support-modal-content ${
        embedded ? 'support-modal-content--embedded' : ''
      }`}
    >
      <h2>{titleFor(supportType)}</h2>

      {response ? (
        <div className="success-message">
          <h3>Thank you for helping to improve IntelliFire!</h3>
          <p>
            Your issue has been logged as:
            <span className="issue-item-link">
              <a href={response.link} target="_blank" rel="noopener noreferrer">
                {response.name}
              </a>
            </span>
          </p>

          {response.link && (
            <p>
              You can track your issue at:
              <span className="issue-item-link">
                <a href={response.link} target="_blank" rel="noopener noreferrer">
                  {response.link}
                </a>
              </span>
            </p>
          )}

          <button onClick={handleCloseModal}>Close</button>
        </div>
      ) : (
        <>
          {supportType === 'openIssues' && openIssuesList}

          {supportType === 'bug' && (
            <>
            <button
              type="button"
              className="support-back-button"
              onClick={() => setSupportType('support')}
            >
              ← Back to Support Request
            </button>
            <form onSubmit={handleSubmit} className="support-form">
              <div className="support-support-form-group">
                <label htmlFor="title">Bug Title:</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="support-form-group">
                <label htmlFor="description">Bug Description:</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="support-form-group">
                <label htmlFor="stepsToReproduce">Steps to Reproduce:</label>
                <textarea
                  id="stepsToReproduce"
                  name="stepsToReproduce"
                  value={formData.stepsToReproduce}
                  onChange={handleInputChange}
                />
              </div>
              <div className="support-form-group">
                <label htmlFor="expectedBehavior">Expected Behavior:</label>
                <textarea
                  id="expectedBehavior"
                  name="expectedBehavior"
                  value={formData.expectedBehavior}
                  onChange={handleInputChange}
                />
              </div>
              <div className="support-form-group">
                <label htmlFor="actualBehavior">Actual Behavior:</label>
                <textarea
                  id="actualBehavior"
                  name="actualBehavior"
                  value={formData.actualBehavior}
                  onChange={handleInputChange}
                />
              </div>
              <div className="support-form-group">
                <label htmlFor="email">Your Email:</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="support-form-group">
                <label htmlFor="name">Your Name:</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <button type="submit" disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit Bug Report'}
              </button>
            </form>
            <div className="support-existing-issues">
              <h3>Existing issues</h3>
              {openIssuesList}
            </div>
            </>
          )}

          {supportType === 'feature' && (
            <>
            <button
              type="button"
              className="support-back-button"
              onClick={() => setSupportType('support')}
            >
              ← Back to Support Request
            </button>
            <form onSubmit={handleSubmit} className="support-form">
              <div className="support-form-group">
                <label htmlFor="title">Feature Title:</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="support-form-group">
                <label htmlFor="description">Feature Description:</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="support-form-group">
                <label htmlFor="email">Your Email:</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="support-form-group">
                <label htmlFor="name">Your Name:</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <button type="submit" disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit Feature Request'}
              </button>
            </form>
            <div className="support-existing-issues">
              <h3>Existing issues</h3>
              {openIssuesList}
            </div>
            </>
          )}

          {supportType === 'support' && (
            <>
              <form onSubmit={handleSupportSubmit} className="support-form">
                <div className="support-form-group">
                  <label htmlFor="title">Subject:</label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="support-form-group">
                  <label htmlFor="description">Description:</label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="support-form-group">
                  <label htmlFor="email">Your Email:</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="support-form-group">
                  <label htmlFor="name">Your Name:</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="support-form-group">
                  <label htmlFor="attachments">Attachments (optional):</label>
                  <input
                    type="file"
                    id="attachments"
                    name="attachments"
                    multiple
                    onChange={handleFileChange}
                  />
                  {attachments.length > 0 && (
                    <ul className="support-attachment-list">
                      {attachments.map((file, index) => (
                        <li key={index}>{file.name}</li>
                      ))}
                    </ul>
                  )}
                  {attachmentError && (
                    <p className="support-attachment-error">{attachmentError}</p>
                  )}
                </div>
                <button type="submit" disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Submit Support Request'}
                </button>
              </form>

              <div className="support-secondary-actions">
                <p>Would you rather report a bug or suggest an improvement?</p>
                <button
                  type="button"
                  className="support-secondary-button"
                  onClick={() => setSupportType('bug')}
                >
                  <FontAwesomeIcon icon={faBug} /> Report a Bug
                </button>
                <button
                  type="button"
                  className="support-secondary-button"
                  onClick={() => setSupportType('feature')}
                >
                  <FontAwesomeIcon icon={faLightbulb} /> Request a Feature
                </button>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );

  // we always just render the content now – menu/context opens the right view
  return content;
};

export default SupportTool;
